import {

  ASPECT_ES,
  ASPECT_EN,

  ASPECT_SYMBOL,

  ASPECT_NATURE_ES,
  ASPECT_NATURE_EN,

  PLANET_MEANING_ES,
  PLANET_MEANING_EN,

  HOUSE_MEANING_ES,
  HOUSE_MEANING_EN,

  SIGN_TONE_ES,
  SIGN_TONE_EN,

  tSign,

  tPlanet,

  planetEmoji,

  signEmoji,

  houseLabel,

} from './natalConstants';
import { isEnglishPresentation } from '../../localization/presentationLanguage';

const localized = (es, en) => (isEnglishPresentation() ? en : es);


// --- Traducciones (EN -> ES) para lo que devuelve el servicio de cartas ---
export function aspectTypeFromItem(item) {
  if (!item || typeof item !== 'object') return '';
  return String(item.type || item.aspectKey || '').trim();
}

export function aspectTitleFromItem(item) {
  if (!item || typeof item !== 'object') return localized('Aspecto', 'Aspect');

  const aRaw = item.a || item.pointA || item.pointALabel;
  const bRaw = item.b || item.pointB || item.pointBLabel;
  const type = aspectTypeFromItem(item);

  if (aRaw && bRaw && type) {
    const a = tPlanet(aRaw);
    const b = tPlanet(bRaw);
    return `${a} ${aspectSymbol(type)} ${b} · ${tAspect(type)}`;
  }

  if (item.title) {
    const rawTitle = String(item.title).trim();
   const match = rawTitle.match(/^(.+?)\s+(conjunction|sextile|square|trine|opposition|conjunción|sextil|cuadratura|trígono|oposición)\s+(.+)$/i);
    if (match) {
      const [, left, rawType, right] = match;
     const rawKey = String(rawType || '').toLowerCase();
const keyMap = {
  'conjunción': 'conjunction',
  'sextil': 'sextile',
  'cuadratura': 'square',
  'trígono': 'trine',
  'oposición': 'opposition'
};
const key = keyMap[rawKey] || rawKey;
      return `${tPlanet(left)} ${aspectSymbol(key)} ${tPlanet(right)} · ${tAspect(key)}`;
    }
    return rawTitle;
  }

  const a = tPlanet(aRaw || localized('Punto A', 'Point A'));
  const b = tPlanet(bRaw || localized('Punto B', 'Point B'));
  return `${a} ${aspectSymbol(type)} ${b} · ${tAspect(type)}`;
}

export function aspectMetaParts(item) {
  if (!item || typeof item !== 'object') return [];
  const parts = [];

  if (typeof item.orb === 'number' && Number.isFinite(item.orb)) {
    parts.push(`${localized('Orbe', 'Orb')} ${item.orb.toFixed(1)}°`);
  }

  if (typeof item.score === 'number' && Number.isFinite(item.score)) {
    parts.push(`${localized('Intensidad', 'Intensity')} ${Math.round(item.score)}/100`);
  }

  const nature = String(item.nature || '').trim();
  const natureLabels = isEnglishPresentation() ? ASPECT_NATURE_EN : ASPECT_NATURE_ES;
  if (nature && natureLabels[nature]) {
    parts.push(natureLabels[nature]);
  }

  return parts;
}

export function aspectTextFromItem(item) {
  if (!item || typeof item !== 'object') return '';
  return String(item.text || item.interpretation || item.narrative || item.premiumInterpretation || '').trim();
}

export function tAspect(type) {
  if (!type) return localized('Aspecto', 'Aspect');
  const key = String(type).trim();
  return (isEnglishPresentation() ? ASPECT_EN : ASPECT_ES)[key] || key;
}

export function aspectSymbol(type) {
  if (!type) return '✶';
  const key = String(type).trim();
  return ASPECT_SYMBOL[key] || '✶';
}

export function ensureArray(v) {
  return Array.isArray(v) ? v : [];
}

// Premium aspect helpers
function clampText(v) {
  return String(v || '').replace(/\s+/g, ' ').trim();
}

function upperFirst(text) {
  const s = String(text || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function splitAspectNarrative(text) {
  const cleaned = clampText(text)
    .replace(/^[^:]{3,120}:\s*/g, '')
    .replace(/\s+Orbe:\s*[0-9.]+°\.?/gi, '')
    .replace(/\s+Intensidad estimada:\s*\d+\/100\.?/gi, '')
    .trim();

  if (!cleaned) return { intro: '', body: '' };

  const parts = cleaned
    .split(/(?<=[.!?])\s+/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    return { intro: upperFirst(cleaned), body: '' };
  }

  return {
    intro: upperFirst(parts[0]),
    body: parts.slice(1).join(' '),
  };
}

export function buildEditorialAspectSections(text) {
  const cleaned = clampText(text)
    .replace(/\s+Orbe:\s*[0-9.]+°\.?/gi, '')
    .replace(/\s+Intensidad estimada:\s*\d+\/100\.?/gi, '')
    .trim();
  const sentences = cleaned
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  const sections = { lead: [], strengths: [], challenges: [], advice: [], detail: [] };
  let leadAssigned = false;
  sentences.forEach((sentence) => {
    const normalized = sentence.toLowerCase();
    const value = upperFirst(sentence)
      .replace(/^(?:Esta|Este)\s+(?:conjunción|oposición|trígono|cuadratura|sextil)\s+muestra\s+/i, '');
    if (/^(fortaleza|fortalezas|potencial|recurso|facilidad|talento)\s*:/i.test(sentence) || /\bfortaleza|potencial|recurso|facilidad|talento\b/.test(normalized)) {
      sections.strengths.push(value.replace(/^[^:]+:\s*/i, ''));
    } else if (/^(desafío|desafíos|sombra|riesgo|tensión)\s*:/i.test(sentence) || /\bdesafío|sombra|riesgo|dificultad\b/.test(normalized)) {
      sections.challenges.push(value.replace(/^[^:]+:\s*/i, ''));
    } else if (/^(clave|consejo|recomendación|integración)\s*:/i.test(sentence) || /\bclave está|conviene|recomendamos|para integrar\b/.test(normalized)) {
      sections.advice.push(value.replace(/^[^:]+:\s*/i, ''));
    } else if (!leadAssigned) {
      sections.lead.push(value);
      leadAssigned = true;
    } else {
      sections.detail.push(value);
    }
  });
  return sections;
}

export function aspectIntensityTier(score) {
  const value = Number(score);
  if (!Number.isFinite(value)) return 'secondary';
  if (value >= 90) return 'very-strong';
  if (value >= 80) return 'strong';
  if (value >= 70) return 'notable';
  return 'secondary';
}

export function prioritizeAspects(aspects) {
  return (Array.isArray(aspects) ? aspects : [])
    .map((aspect, index) => ({ aspect, index }))
    .sort((left, right) => {
      const leftScore = Number(left.aspect?.score);
      const rightScore = Number(right.aspect?.score);
      const scoreDelta = (Number.isFinite(rightScore) ? rightScore : -Infinity)
        - (Number.isFinite(leftScore) ? leftScore : -Infinity);
      if (scoreDelta !== 0) return scoreDelta;
      const leftOrb = Number(left.aspect?.orb);
      const rightOrb = Number(right.aspect?.orb);
      const orbDelta = (Number.isFinite(leftOrb) ? leftOrb : Infinity)
        - (Number.isFinite(rightOrb) ? rightOrb : Infinity);
      return orbDelta || left.index - right.index;
    })
    .map(({ aspect }) => aspect);
}


export function aspectToneMeta(item) {
  const harmony = Number(item?.harmony);
  const type = String(item?.type || item?.aspectKey || '').trim().toLowerCase();

  if (Number.isFinite(harmony)) {
    if (harmony > 0) {
      return {
        label: type === 'conjunction' ? localized('Fusión armónica', 'Harmonious fusion') : localized('Flujo armónico', 'Harmonious flow'),
        kind: type === 'conjunction' ? 'fusion' : 'soft',
      };
    }
    if (harmony < 0) {
      return {
        label: type === 'conjunction' ? localized('Fusión desafiante', 'Challenging fusion') : localized('Tensión evolutiva', 'Growth tension'),
        kind: 'hard',
      };
    }
    return {
      label: type === 'conjunction' ? localized('Fusión intensa', 'Intense fusion') : localized('Aspecto activo', 'Active aspect'),
      kind: type === 'conjunction' ? 'fusion' : 'neutral',
    };
  }

  if (type === 'conjunction') {
    return { label: localized('Fusión intensa', 'Intense fusion'), kind: 'fusion' };
  }

  const nature = String(item?.nature || '').trim();
  if (nature === 'soft') return { label: localized('Flujo armónico', 'Harmonious flow'), kind: 'soft' };
  if (nature === 'hard' || nature === 'tense') return { label: localized('Tensión evolutiva', 'Growth tension'), kind: 'hard' };
  if (nature === 'awkward') return { label: localized('Ajuste fino', 'Fine adjustment'), kind: 'awkward' };

  return { label: localized('Aspecto activo', 'Active aspect'), kind: 'neutral' };
}

export function aspectBadgeStyle(kind) {
  if (kind === 'fusion') {
    return {
      background: 'rgba(218, 165, 32, 0.16)',
      border: '1px solid rgba(218, 165, 32, 0.30)',
      color: '#FFE7A3',
    };
  }

  if (kind === 'soft') {
    return {
      background: 'rgba(89, 208, 154, 0.14)',
      border: '1px solid rgba(89, 208, 154, 0.28)',
      color: '#B9FFD7',
    };
  }

  if (kind === 'hard') {
    return {
      background: 'rgba(255, 138, 138, 0.14)',
      border: '1px solid rgba(255, 138, 138, 0.30)',
      color: '#FFD0D0',
    };
  }

  if (kind === 'awkward') {
    return {
      background: 'rgba(255, 214, 102, 0.14)',
      border: '1px solid rgba(255, 214, 102, 0.28)',
      color: '#FFE8A6',
    };
  }

  return {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: '#F4E7BF',
  };
}

export function renderBulletChipText(text) {
  const s = String(text || '').trim();
  if (!s) return '';
  return s.replace(/\.$/, '');
}

export function isStructuredInterpretationLine(text) {
  const s = stripMd(text || '').trim();
  if (!s) return false;

  return /^(✨|🎭|🏛|🌌|⭐|🌑|🌱)\s*/u.test(s)
    || /^(Esencia|Expresión|Área activada|Integración|Fortalezas naturales|Desafío o sombra|Clave de evolución)\s*:/i.test(s);
}

export function buildPatternRowsFromBullets(bullets) {
  const list = ensureArray(bullets)
    .map((x) => stripMd(String(x || '')).trim())
    .filter(Boolean);

  // Remove duplicated headings coming from the backend
  const IGNORED_LINES = [
    /^✦\s*Patrones principales/i,
    /^Lectura rápida de tus focos más visibles/i,
    /^Lectura estructural de la carta/i
  ];

  const cleanedList = list.filter((l) => !IGNORED_LINES.some((r) => r.test(l)));

  if (!cleanedList.length) return [];

  const cleanValueText = (text, { isStelliums = false } = {}) => {
    let value = String(text || '').trim();
    if (!value) return '';

    value = value
      .replace(/^•\s*/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (isStelliums) {
      value = value
        .replace(/[🌌🏛]+\s*/g, '')
        .replace(/Casa\s*1\s*\/\s*Casa\s*12/gi, 'Casa 1. Casa 12')
        .replace(/Casa\s*12\s*\/\s*Casa\s*1/gi, 'Casa 12. Casa 1')
        // Stelliums formatting: cleaner version with centered separator
        .replace(/Casa\s*(\d{1,2})/gi, (_, n) => `🏛 Casa ${Number(n)}`)
        .replace(/(🏛 Casa \d{1,2})(?=\s+🏛 Casa \d{1,2})/g, '$1 · ')
        .replace(/\s*·\s*/g, ' · ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    return value;
  };

  const rows = [];
  let current = null;

  for (const raw of cleanedList) {
    const line = String(raw || '').trim();
    if (!line) continue;

    const labelMatch = line.match(/^(Dominante|Luminaria emocional|Stelliums|Eje evolutivo):\s*(.*)$/i);

    if (labelMatch) {
      if (current) rows.push(current);

      const label = `${labelMatch[1]}:`;
      const isStelliums = /^stelliums:$/i.test(label);
      const value = cleanValueText(labelMatch[2] || '', { isStelliums });

      current = { label, value };
      continue;
    }

    if (current) {
      const isStelliums = /^stelliums:$/i.test(current.label);
      const merged = current.value ? `${current.value} ${line}` : line;
      current.value = cleanValueText(merged, { isStelliums });
    }
  }

  if (current) rows.push(current);
  return rows;
}

export function safeIntegratedTitle(v, fallback) {
  const s = String(v || '').trim();
  if (!s) return '';
  if (s === fallback) return '';
  return s;
}
export function safeIntegratedSubtitle(v, fallback) {
  const s = String(v || '').trim();
  if (!s) return '';
  if (s === fallback) return '';
  return s;
}
// --- Premium renderer helpers (interpretación) ---
export function stripMd(v) {
  return String(v || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .trim();
}

export function splitLines(v) {
  const s = String(v || '');
  return s
    .split('\n')
    .map((x) => stripMd(x))
    .map((x) => x.trim())
    .filter((x) => {
      if (!x) return false;
      if (isDecorativeOnlyLine(x)) return false;
      if (x.startsWith('✦')) return false;

      // Evita repetir encabezados de planeta (ya aparecen en el título de la card)
      const cleaned = normalizePlanetHeaderLine(x);
      const planetHeader = cleaned.match(/^(Ascendente|Sol|Luna|Mercurio|Venus|Marte|Júpiter|Saturno|Urano|Neptuno|Plutón|Medio Cielo|Nodo Norte|Nodo Sur|Lilith|Quirón)\b/i);
      if (planetHeader) return false;

      // También descarta líneas que empiezan con glifos planetarios
      if (/^(☀️|☀|🌙|☿|♀|♂|♃|♄|♅|♆|♇|☊|☋|⚸|⚷|↗|🜁)\s*/u.test(x)) return false;

      return true;
    });
}

export function lineKind(line) {
  const l = String(line || '');
  const key = l.toLowerCase();

  if (
    key.startsWith('✨ esencia:') ||
    key.startsWith('esencia:') ||
    key.startsWith('🔥 qué energía es:') ||
    key.startsWith('qué energía es:')
  ) return 'energy';

  if (
    key.startsWith('🎭 expresión:') ||
    key.startsWith('expresión:') ||
    key.startsWith('🎭 cómo se expresa:') ||
    key.startsWith('cómo se expresa:')
  ) return 'express';

  if (
    key.startsWith('🏛 área activada:') ||
    key.startsWith('área activada:') ||
    key.startsWith('🧭 área de tu vida:') ||
    key.startsWith('área de tu vida:') ||
    key.startsWith('en qué área de tu vida:')
  ) return 'area';

  if (
    key.startsWith('🌌 integración:') ||
    key.startsWith('integración:') ||
    key.startsWith('interpretación integrada:')
  ) return 'integrate';

  if (
    key.startsWith('⭐ fortalezas naturales:') ||
    key.startsWith('fortalezas naturales:') ||
    key.startsWith('⭐ fortalezas:') ||
    key.startsWith('fortalezas:')
  ) return 'strength';

  if (
    key.startsWith('🌑 desafío o sombra:') ||
    key.startsWith('desafío o sombra:') ||
    key.startsWith('🌑 sombra:') ||
    key.startsWith('sombra:')
  ) return 'shadow';

  if (
    key.startsWith('🌱 clave de evolución:') ||
    key.startsWith('clave de evolución:') ||
    key.startsWith('🌱 evolución:') ||
    key.startsWith('evolución:')
  ) return 'growth';

  return 'text';
}

export function kindMeta(kind) {
  switch (kind) {
    case 'energy': return { label: localized('Esencia', 'Essence'), icon: '✨' };
    case 'express': return { label: localized('Expresión', 'Expression'), icon: '🎭' };
    case 'area': return { label: localized('Área activada', 'Activated area'), icon: '🏛' };
    case 'integrate': return { label: localized('Integración', 'Integration'), icon: '🌌' };
    case 'strength': return { label: localized('Fortalezas naturales', 'Natural strengths'), icon: '⭐' };
    case 'shadow': return { label: localized('Desafío o sombra', 'Challenge or shadow'), icon: '🌑' };
    case 'growth': return { label: localized('Clave de evolución', 'Growth key'), icon: '🌱' };
    default: return { label: '', icon: '' };
  }
}

export function buildCompactPlanetEditorial(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const valueFor = (kind) => {
    const row = safeRows.find((item) => item?.kind === kind && item?.line);
    return row ? stripPrefix(row.line) : '';
  };
  const primary = valueFor('integrate') || valueFor('energy') || valueFor('express') || stripPrefix(safeRows.find((item) => item?.line)?.line || '');
  const strength = valueFor('strength');
  const challenge = valueFor('shadow');
  const strengthChips = strength
    .split(/[,;•|]/)
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 4);
  return { primary, strengthChips, challenge };
}

export function compactPlacementDegree(value) {
  const match = String(value || '').match(/([0-9]+(?:[.,][0-9]+)?)\s*°/);
  if (!match) return '';
  const absolute = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(absolute)) return '';
  return `${(absolute % 30).toFixed(1)}°`;
}

export function buildExpandedPlanetEditorial(rows, compact = buildCompactPlanetEditorial(rows)) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const valueFor = (kind) => {
    const row = safeRows.find((item) => item?.kind === kind && item?.line);
    return row ? stripPrefix(row.line) : '';
  };
  const energy = valueFor('energy');
  const expression = valueFor('express');
  const expressionLead = /^en\b/i.test(expression) ? expression : `en vos, ${expression.charAt(0).toLowerCase()}${expression.slice(1)}`;
  const howItWorks = [energy, expression].filter(Boolean).length > 1
    ? `${energy.replace(/[.。]\s*$/, '')}. ${expressionLead}`
    : energy || expression;
  const whereItManifests = valueFor('area')
    .replace(/^La Casa\s*\d+\s+(describe|representa)\s+/i, 'En tu carta, esta energía se manifiesta ')
    .replace(/^Casa\s*\d+\s+(describe|representa)\s+/i, 'En tu carta, esta energía se manifiesta ');
  const integration = valueFor('integrate');
  const nuance = integration && integration !== compact.primary ? integration : '';
  const evolution = valueFor('growth');
  const fallback = safeRows
    .filter((item) => item?.kind === 'text' && item?.line)
    .map((item) => stripPrefix(item.line))
    .filter(Boolean)
    .slice(0, 2)
    .join(' ');
  return {
    howItWorks: howItWorks || fallback,
    whereItManifests,
    nuance,
    evolution,
  };
}

export function stripPrefix(line) {
  const s = String(line || '');
  return s
    .replace(/^✨\s*/, '')
    .replace(/^🔥\s*/, '')
    .replace(/^🎭\s*/, '')
    .replace(/^🏛\s*/, '')
    .replace(/^🧭\s*/, '')
    .replace(/^🌌\s*/, '')
    .replace(/^⭐\s*/, '')
    .replace(/^🌑\s*/, '')
    .replace(/^🌱\s*/, '')
    .replace(/^\s*Esencia:\s*/i, '')
    .replace(/^\s*Qué energía es:\s*/i, '')
    .replace(/^\s*Expresión:\s*/i, '')
    .replace(/^\s*Cómo se expresa:\s*/i, '')
    .replace(/^\s*Área activada:\s*/i, '')
    .replace(/^\s*Área de tu vida:\s*/i, '')
    .replace(/^\s*En qué área de tu vida:\s*/i, '')
    .replace(/^\s*Integración:\s*/i, '')
    .replace(/^\s*Interpretación integrada:\s*/i, '')
    .replace(/^\s*Fortalezas naturales:\s*/i, '')
    .replace(/^\s*Fortalezas:\s*/i, '')
    .replace(/^\s*Desafío o sombra:\s*/i, '')
    .replace(/^\s*Sombra:\s*/i, '')
    .replace(/^\s*Clave de evolución:\s*/i, '')
    .replace(/^\s*Evolución:\s*/i, '')
    .trim();
}
export function normalizePlanetHeaderLine(line) {
  return String(line || '')
    .replace(/^✦\s*/u, '')
    .replace(/^[☀☀️🌙☿♀♂♃♄♅♆♇☊☋⚸⚷↗🜁]\s*/u, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isDecorativeOnlyLine(line) {
  const raw = String(line || '').trim();
  if (!raw) return true;
  const cleaned = raw.replace(/[\uFE0F\u200D]/g, '').trim();
  return /^✦+$/.test(cleaned);
}

function extractHouseNumber(text) {
  const match = String(text || '').match(/Casa\s*(\d{1,2})/i);
  return match ? Number(match[1]) : null;
}

export function decoratePlanetCardTitle(title, subtitle) {
  const rawTitle = stripMd(title || '').trim();
  const cleanTitle = normalizePlanetHeaderLine(rawTitle);
  const houseNum = extractHouseNumber(subtitle || title || '');
  const houseTag = houseNum ? houseLabel(houseNum) : '';

  const match = cleanTitle.match(
    /^(Ascendente|Sol|Luna|Mercurio|Venus|Marte|Júpiter|Saturno|Urano|Neptuno|Plutón|Medio Cielo \(MC\)|Nodo Norte \(verdadero\)|Nodo Sur|Lilith \(Luna Negra\)|Quirón)\s+en\s+([A-Za-zÁÉÍÓÚáéíóúñÑ]+)(.*)$/i
  );

  if (!match) {
    return houseTag ? `${cleanTitle}\u202F·\u202F${houseTag}` : cleanTitle;
  }

  const [, rawPlanetLabel, rawSignLabel, trailing] = match;

  const planetKeyMap = {
    Ascendente: 'Ascendant',
    Sol: 'Sun',
    Luna: 'Moon',
    Mercurio: 'Mercury',
    Venus: 'Venus',
    Marte: 'Mars',
    Júpiter: 'Jupiter',
    Saturno: 'Saturn',
    Urano: 'Uranus',
    Neptuno: 'Neptune',
    Plutón: 'Pluto',
    'Medio Cielo (MC)': 'MC',
    'Nodo Norte (verdadero)': 'True Node',
    'Nodo Sur': 'South Node',
    'Lilith (Luna Negra)': 'Lilith',
    Quirón: 'Chiron',
  };

  const signKeyMap = {
    Aries: 'Aries',
    Tauro: 'Taurus',
    Géminis: 'Gemini',
    Cancer: 'Cancer',
    Cáncer: 'Cancer',
    Leo: 'Leo',
    Virgo: 'Virgo',
    Libra: 'Libra',
    Escorpio: 'Scorpio',
    Sagitario: 'Sagittarius',
    Capricornio: 'Capricorn',
    Acuario: 'Aquarius',
    Piscis: 'Pisces',
  };

  const planetKey = planetKeyMap[rawPlanetLabel] || '';
  const signKey = signKeyMap[rawSignLabel] || '';
  const pEmoji = planetEmoji(planetKey);
  const sEmoji = signEmoji(signKey);

  const nextTitle = `${pEmoji ? `${pEmoji} ` : ''}${sEmoji ? `${sEmoji} ` : ''}${tPlanet(planetKey || rawPlanetLabel)} ${localized('en', 'in')} ${tSign(signKey || rawSignLabel)}${String(
    trailing || ''
  ).trimEnd()}`.trim();

  return houseTag ? `${nextTitle}\u202F·\u202F${houseTag}` : nextTitle;
}

// --- Interpretaciones locales (fallback) ---
// Mínimo viable: siempre hay texto debajo de la carta aunque el backend no devuelva interpretaciones.

export function buildLocalInterpretationsFromChart(chart) {
  if (!chart || !Array.isArray(chart.planets)) {
    return { summary: null, interpretations: null };
  }

  const sunSign = chart.sun?.sign;
  const moonSign = chart.moon?.sign;
  const ascSign = chart.ascendant?.sign;

  const summaryText = isEnglishPresentation()
    ? `Your energetic foundation combines ${sunSign ? `${tSign(sunSign)} (Sun)` : 'your Sun'} ${moonSign ? `and ${tSign(moonSign)} (Moon)` : ''}${ascSign ? `, with the Ascendant in ${tSign(ascSign)}` : ''}. This describes your identity, emotional world, and how you present yourself to others.`
    : `Tu base energética combina ${sunSign ? `${tSign(sunSign)} (Sol)` : 'tu Sol'} ${moonSign ? `y ${tSign(moonSign)} (Luna)` : ''}${ascSign ? `, con Ascendente en ${tSign(ascSign)}` : ''}. Esto describe tu identidad, tu mundo emocional y cómo te presentás al mundo.`;

  const planets = chart.planets
    .filter((p) => p && (p.name || p.planet))
    .map((p, idx) => {
      const rawName = p.name || p.planet;
      const sign = p.sign;
      const house = Number(p.house);

      const planetMeaning = (isEnglishPresentation() ? PLANET_MEANING_EN : PLANET_MEANING_ES)[rawName]
        || localized('Influencia principal de este punto en tu carta.', 'The main influence of this point in your chart.');
      const houseMeaning = (isEnglishPresentation() ? HOUSE_MEANING_EN : HOUSE_MEANING_ES)[house]
        || localized('Área de vida activada por esta energía.', 'The area of life activated by this energy.');
      const tone = (isEnglishPresentation() ? SIGN_TONE_EN : SIGN_TONE_ES)[sign]
        || localized('con un matiz particular', 'with its own distinctive tone');

      const title = `${p.symbol ? `${p.symbol} ` : ''}${tPlanet(rawName)} ${localized('en', 'in')} ${tSign(
        sign
      )} · ${houseLabel(Number.isFinite(house) ? house : null)}`;

      const text = isEnglishPresentation()
        ? `${planetMeaning} In ${tSign(sign)}, it is expressed ${tone}. In House ${Number.isFinite(house) ? house : '—'}, it manifests through: ${houseMeaning}`
        : `${planetMeaning} En ${tSign(sign)} se expresa ${tone}. En Casa ${Number.isFinite(house) ? house : '—'} se manifiesta en: ${houseMeaning}`;

      return { key: `${rawName || 'p'}-${idx}`, title, text };
    });

  return {
    summary: { text: summaryText },
    interpretations: { planets },
  };
}
